import { useState, useEffect } from 'react'
import { useFilters } from '../contexts/FilterContext'
import { api } from '../services/api'
import { Filter, Clock, CheckCircle, Users, AlertCircle, TrendingUp, TrendingDown } from 'lucide-react'
import styles from './Funil.module.css'

interface FunilEtapa {
  nome: string
  quantidade: number
  percentual: number
}

interface TaxasConversao {
  lead_pre: number
  pre_reserva: number
  reserva_venda: number
  lead_venda: number
}

interface FunilData {
  etapas: FunilEtapa[]
  taxas_conversao: TaxasConversao
}

interface TempoMedioEtapa {
  de: string
  para: string
  tempo_medio_dias: number
  tempo_formatado: string
}

interface TempoMedioResponse {
  etapas: TempoMedioEtapa[]
  tempo_total_medio: number
}

interface LossData {
  nome: string
  quantidade: number
  percentual: number
}

interface FunilComparativo {
  periodo_atual: {
    data_inicio: string
    data_fim: string
    etapas: FunilEtapa[]
    taxas_conversao: TaxasConversao
  }
  periodo_anterior: {
    data_inicio: string
    data_fim: string
    etapas: FunilEtapa[]
    taxas_conversao: TaxasConversao
  }
  variacoes: Array<{ nome: string; variacao: number }>
}

export function Funil() {
  const { queryParams, filtersVersion } = useFilters()
  const [funilData, setFunilData] = useState<FunilData | null>(null)
  const [funilComparativo, setFunilComparativo] = useState<FunilComparativo | null>(null)
  const [tempoMedio, setTempoMedio] = useState<TempoMedioResponse | null>(null)
  const [viewMode, setViewMode] = useState<'single' | 'compare'>('single')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dados de perdas carregados da API
  const [perdasPorCorretor, setPerdasPorCorretor] = useState<LossData[]>([])
  const [perdasPorMotivo, setPerdasPorMotivo] = useState<LossData[]>([])

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        const [funilResponse, tempoResponse, comparativoResponse] = await Promise.all([
          api.getVendasFunil(queryParams),
          api.getLeadsTempoMedioEtapas(queryParams),
          api.getVendasFunilComparativo(queryParams),
        ])

        setFunilData(funilResponse as unknown as FunilData)
        setTempoMedio(tempoResponse as unknown as TempoMedioResponse)
        setFunilComparativo(comparativoResponse as FunilComparativo)

        // Carrega dados de perdas
        try {
          const perdasData = await api.getLeadsAnalisePerdas(queryParams)
          if (perdasData) {
            // Perdas por corretor
            if (perdasData.por_corretor) {
              setPerdasPorCorretor(perdasData.por_corretor.slice(0, 5).map((c: any) => ({
                nome: c.corretor || 'Não Atribuído',
                quantidade: c.quantidade || 0,
                percentual: c.percentual || 0,
              })))
            }
            // Perdas por motivo de descarte
            if (perdasData.por_motivo) {
              setPerdasPorMotivo(perdasData.por_motivo.slice(0, 5).map((m: any) => ({
                nome: m.motivo || 'Não Informado',
                quantidade: m.quantidade || 0,
                percentual: m.percentual || 0,
              })))
            }
          }
        } catch (e) {
          // Sem fallback - mostra vazio se não houver dados
          setPerdasPorCorretor([])
          setPerdasPorMotivo([])
        }
      } catch (err) {
        console.error('Erro ao carregar funil:', err)
        setError('Erro ao carregar dados do funil')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [queryParams, filtersVersion])

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value)
  }

  // Get stage color based on index
  const getStageColor = (index: number) => {
    const colors = [
      'var(--color-primary-500)',
      'var(--color-primary-400)',
      'var(--color-warning-500)',
      'var(--color-success-500)',
    ]
    return colors[index % colors.length]
  }

  // Get stage width based on percentage
  const getStageWidth = (percentual: number) => {
    const minWidth = 40
    const maxWidth = 100
    return Math.max(minWidth, Math.min(maxWidth, percentual))
  }

  const totalPerdas = perdasPorCorretor.reduce((acc, item) => acc + item.quantidade, 0)
  const taxaPerdas = funilData && funilData.etapas.length > 0
    ? ((totalPerdas / funilData.etapas[0].quantidade) * 100).toFixed(1)
    : '0'

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Funil de Vendas</h1>
            <p className={styles.pageSubtitle}>Analise de conversao por etapa do funil</p>
          </div>
        </div>
        <div className={styles.errorState}>
          <p>{error}</p>
          <p>Verifique se a API esta funcionando corretamente.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Carregando dados do funil...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Funil de Vendas</h1>
          <p className={styles.pageSubtitle}>Analise completa de conversao por etapa do funil</p>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewBtn} ${viewMode === 'single' ? styles.active : ''}`}
              onClick={() => setViewMode('single')}
            >
              <Filter size={16} />
              Unico
            </button>
            <button
              className={`${styles.viewBtn} ${viewMode === 'compare' ? styles.active : ''}`}
              onClick={() => setViewMode('compare')}
            >
              <Filter size={16} />
              Comparar Periodos
            </button>
          </div>
        </div>
      </div>

      {/* Single Funnel View */}
      {viewMode === 'single' && (
        <section className={styles.section}>
          <div className={styles.funnelContainer}>
            <div className={styles.funnelVisual}>
              {funilData && funilData.etapas.map((etapa, index) => (
                <div key={etapa.nome}>
                  <div className={styles.funnelStage}>
                    <div
                      className={styles.funnelBar}
                      style={{
                        width: `${getStageWidth(etapa.percentual)}%`,
                        backgroundColor: getStageColor(index),
                      }}
                    >
                      <div className={styles.funnelInfo}>
                        <span className={styles.funnelLabel}>{etapa.nome.toUpperCase()}</span>
                        <span className={styles.funnelDesc}>
                          {index === 0 && 'Total de leads repassados pelo marketing'}
                          {index === 1 && 'Leads que preencheram dados completos'}
                          {index === 2 && 'Interessados que reservaram unidades'}
                          {index === 3 && 'Contratos assinados e consolidados'}
                        </span>
                      </div>
                      <div className={styles.funnelNumbers}>
                        <span className={styles.funnelValue}>{formatNumber(etapa.quantidade)}</span>
                        <span className={styles.funnelPercent}>{etapa.percentual}%</span>
                      </div>
                    </div>
                  </div>
                  {index < funilData.etapas.length - 1 && (
                    <div className={styles.funnelArrow}>
                      <span className={`${styles.funnelConversionRate} ${index === funilData.etapas.length - 2 ? styles.success : ''}`}>
                        {index === 0 && `${funilData.taxas_conversao.lead_pre}% converteram`}
                        {index === 1 && `${funilData.taxas_conversao.pre_reserva}% converteram`}
                        {index === 2 && `${funilData.taxas_conversao.reserva_venda}% converteram`}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comparison View */}
      {viewMode === 'compare' && funilComparativo && (
        <section className={styles.section}>
          <div className={styles.funnelComparison}>
            {/* Current Period */}
            <div className={styles.funnelColumn}>
              <div className={styles.funnelColumnHeader}>
                <h3 className={styles.funnelColumnTitle}>Periodo Atual</h3>
                <span className={styles.funnelColumnDates}>
                  {new Date(funilComparativo.periodo_atual.data_inicio).toLocaleDateString('pt-BR')} - {new Date(funilComparativo.periodo_atual.data_fim).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className={`${styles.funnelContainer} ${styles.funnelCompact}`}>
                <div className={styles.funnelVisual}>
                  {funilComparativo.periodo_atual.etapas.map((etapa, index) => (
                    <div key={etapa.nome}>
                      <div className={styles.funnelStage}>
                        <div
                          className={styles.funnelBar}
                          style={{
                            width: `${getStageWidth(etapa.percentual)}%`,
                            backgroundColor: getStageColor(index),
                          }}
                        >
                          <div className={styles.funnelInfo}>
                            <span className={styles.funnelLabel}>{etapa.nome.toUpperCase()}</span>
                          </div>
                          <div className={styles.funnelNumbers}>
                            <span className={styles.funnelValue}>{formatNumber(etapa.quantidade)}</span>
                          </div>
                        </div>
                      </div>
                      {index < funilComparativo.periodo_atual.etapas.length - 1 && (
                        <div className={styles.funnelArrowSm}>
                          <span className={`${styles.conversionBadge} ${index === funilComparativo.periodo_atual.etapas.length - 2 ? styles.success : ''}`}>
                            {index === 0 && `${funilComparativo.periodo_atual.taxas_conversao.lead_pre}%`}
                            {index === 1 && `${funilComparativo.periodo_atual.taxas_conversao.pre_reserva}%`}
                            {index === 2 && `${funilComparativo.periodo_atual.taxas_conversao.reserva_venda}%`}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.funnelSummary}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Conversao Total</span>
                  <span className={styles.summaryValue}>{funilComparativo.periodo_atual.taxas_conversao.lead_venda || 0}%</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Ciclo Medio</span>
                  <span className={styles.summaryValue}>{tempoMedio?.tempo_total_medio?.toFixed(1) ?? '0.0'} dias</span>
                </div>
              </div>
            </div>

            {/* Variation Indicators - Dados reais da API */}
            <div className={styles.funnelVariation}>
              {funilComparativo.variacoes.map((v) => (
                <div key={v.nome} className={`${styles.variationItem} ${v.variacao >= 0 ? styles.positive : styles.negative}`}>
                  <span className={styles.variationLabel}>{v.nome}</span>
                  <span className={styles.variationValue}>{v.variacao >= 0 ? '+' : ''}{v.variacao}%</span>
                  {v.variacao >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                </div>
              ))}
            </div>

            {/* Previous Period */}
            <div className={styles.funnelColumn}>
              <div className={styles.funnelColumnHeader}>
                <h3 className={styles.funnelColumnTitle}>Periodo Anterior</h3>
                <span className={styles.funnelColumnDates}>
                  {new Date(funilComparativo.periodo_anterior.data_inicio).toLocaleDateString('pt-BR')} - {new Date(funilComparativo.periodo_anterior.data_fim).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <div className={`${styles.funnelContainer} ${styles.funnelCompact}`}>
                <div className={styles.funnelVisual}>
                  {funilComparativo.periodo_anterior.etapas.map((etapa, index) => (
                    <div key={etapa.nome}>
                      <div className={styles.funnelStage}>
                        <div
                          className={styles.funnelBar}
                          style={{
                            width: `${getStageWidth(etapa.percentual)}%`,
                            backgroundColor: 'var(--color-neutral-400)',
                          }}
                        >
                          <div className={styles.funnelInfo}>
                            <span className={styles.funnelLabel}>{etapa.nome.toUpperCase()}</span>
                          </div>
                          <div className={styles.funnelNumbers}>
                            <span className={styles.funnelValue}>{formatNumber(etapa.quantidade)}</span>
                          </div>
                        </div>
                      </div>
                      {index < funilComparativo.periodo_anterior.etapas.length - 1 && (
                        <div className={styles.funnelArrowSm}>
                          <span className={`${styles.conversionBadge} ${styles.neutral}`}>
                            {index === 0 && `${funilComparativo.periodo_anterior.taxas_conversao.lead_pre}%`}
                            {index === 1 && `${funilComparativo.periodo_anterior.taxas_conversao.pre_reserva}%`}
                            {index === 2 && `${funilComparativo.periodo_anterior.taxas_conversao.reserva_venda}%`}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className={styles.funnelSummary}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>Conversao Total</span>
                  <span className={styles.summaryValue}>{funilComparativo.periodo_anterior.taxas_conversao.lead_venda || 0}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Table */}
          <div className={styles.tableContainer} style={{ marginTop: 'var(--spacing-6)' }}>
            <h3 className={styles.sectionTitle}>Detalhamento do Periodo</h3>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Etapa</th>
                  <th>Atual</th>
                  <th>Anterior</th>
                  <th>Variacao</th>
                </tr>
              </thead>
              <tbody>
                {funilComparativo.periodo_atual.etapas.map((etapa, index) => {
                  const etapaAnterior = funilComparativo.periodo_anterior.etapas[index]
                  const variacao = funilComparativo.variacoes[index]
                  return (
                    <tr key={etapa.nome}>
                      <td>{etapa.nome}</td>
                      <td>{formatNumber(etapa.quantidade)}</td>
                      <td>{formatNumber(etapaAnterior?.quantidade || 0)}</td>
                      <td className={variacao?.variacao >= 0 ? styles.positive : styles.negative}>
                        {variacao?.variacao >= 0 ? '+' : ''}{variacao?.variacao || 0}%
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Time Metrics */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Tempo Medio entre Etapas</h2>
        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              <Clock size={24} />
            </div>
            <span className={styles.metricLabel}>Lead para Pre-Cadastro</span>
            <span className={styles.metricValue}>
              {tempoMedio?.etapas?.find(e => e.de === 'Lead' && e.para === 'Pré-Cadastro')?.tempo_medio_dias?.toFixed(1) || '0'} dias
            </span>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              <Clock size={24} />
            </div>
            <span className={styles.metricLabel}>Pre-Cadastro para Reserva</span>
            <span className={styles.metricValue}>
              {tempoMedio?.etapas?.find(e => e.de === 'Pré-Cadastro' && e.para === 'Reserva')?.tempo_medio_dias?.toFixed(1) || '0'} dias
            </span>
          </div>
          <div className={styles.metricCard}>
            <div className={styles.metricIcon}>
              <Clock size={24} />
            </div>
            <span className={styles.metricLabel}>Lead para Venda</span>
            <span className={styles.metricValue}>
              {tempoMedio?.etapas?.find(e => e.de === 'Lead' && e.para === 'Venda')?.tempo_medio_dias?.toFixed(1) || '0'} dias
            </span>
          </div>
          <div className={`${styles.metricCard} ${styles.highlight}`}>
            <div className={styles.metricIcon} style={{ backgroundColor: 'var(--color-success-100)', color: 'var(--color-success-600)' }}>
              <CheckCircle size={24} />
            </div>
            <span className={styles.metricLabel}>Ciclo Completo (Total)</span>
            <span className={styles.metricValue}>
              {tempoMedio?.tempo_total_medio?.toFixed(1) ?? '0.0'} dias
            </span>
          </div>
        </div>
      </section>

      {/* Loss Analysis */}
      <section className={styles.section}>
        <div className={styles.sectionHeaderFlex}>
          <div>
            <h2 className={styles.sectionTitle}>Analise de Leads Descartados</h2>
            <p className={styles.sectionSubtitle}>Leads perdidos no periodo selecionado</p>
          </div>
          <div className={styles.lossTotalBadge}>
            <span className={styles.lossTotalNumber}>{totalPerdas}</span>
            <span className={styles.lossTotalText}>leads descartados ({taxaPerdas}%)</span>
          </div>
        </div>

        <div className={styles.lossAnalysisTwoCols}>
          {/* Top Corretores with most discards */}
          <div className={styles.lossRankingCard}>
            <div className={styles.lossRankingHeader}>
              <div className={`${styles.lossRankingIcon} ${styles.warning}`}>
                <Users size={20} />
              </div>
              <div>
                <h3 className={styles.lossRankingTitle}>Top 5 Corretores</h3>
                <p className={styles.lossRankingSubtitle}>Com mais leads descartados</p>
              </div>
            </div>
            <div className={styles.lossRankingList}>
              {perdasPorCorretor.map((item, index) => (
                <div key={index} className={styles.lossRankingItem}>
                  <span className={styles.lossRankingName}>{item.nome}</span>
                  <span className={styles.lossRankingValue}>{item.quantidade} ({item.percentual}%)</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Motivos de Descarte */}
          <div className={styles.lossRankingCard}>
            <div className={styles.lossRankingHeader}>
              <div className={`${styles.lossRankingIcon} ${styles.danger}`}>
                <AlertCircle size={20} />
              </div>
              <div>
                <h3 className={styles.lossRankingTitle}>Top 5 Motivos</h3>
                <p className={styles.lossRankingSubtitle}>Motivos de descarte mais usados</p>
              </div>
            </div>
            <div className={styles.lossRankingList}>
              {perdasPorMotivo.map((item, index) => (
                <div key={index} className={styles.lossRankingItem}>
                  <span className={styles.lossRankingName}>{item.nome}</span>
                  <span className={styles.lossRankingValue}>{item.quantidade} ({item.percentual}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
